import React, { useCallback, useRef, useState, useEffect } from 'react';
import './speaker-game.css';
import { SpeakerAnimation } from '../../../animations/speaker-animation';
import { IconButton } from '../../../components/icon-button';
import classNames from 'classnames';
import { selectRandomArrayEntry } from '../../../helpers/active-game';
import { TutorialModalParent } from '../../../components/modal-parents/tutorial-modal';
import { PerformanceTracker } from '../../../components/performance-tracker';
import { Timer } from '../../../components/timer';
import { Paragraph } from '../../../components/paragraph';
const pointDirections = ['forward', 'right', 'rightUp', 'left', 'leftUp'];

const SpeakerGameScreen = props => {
    const {
        accessibleVersion,
        tutorialCompleted,
        completeSpeakerTutorial,
        completeSpeakerGame,
        goBack,
        goToSettings,
        questionsGame,
        historyScreenState,
        setCurrentGameScores,
        setResponseFeedback,
    } = props;

    const speakerAnimation = useRef(new SpeakerAnimation());

    const gameTimerRef = useRef(null);
    const speakerAnimationTimerRef = useRef(null);
    const speakerButtons = useRef(null);

    const [activeHead, setActiveHead] = useState('forward');

    const timerLength = 60;
    const [gameTimer, setGameTimer] = useState(60);
    const [speakerAnimationTimer, setSpeakerAnimationTimer] = useState(0);

    const [clickCounter, setClickCounter] = useState(0);
    const [argumentsCompleted, setArgumentsCompleted] = useState(0);

    const [progressUpdateVisible, setProgressUpdateVisible] = useState(false);

    const modalVisible = !tutorialCompleted || progressUpdateVisible;

    const animationTimeMultiplier = questionsGame === true ? 750 : 1000;

    const initialiseCanvas = useCallback(
        ref => {
            if (ref !== null) {
                speakerAnimation.current.initialise(
                    'speakerCanvas',
                    questionsGame,
                );
            }
        },
        [questionsGame],
    );

    useEffect(() => {
        if (historyScreenState) {
            if (historyScreenState.state.responseFeedback) {
                setResponseFeedback(historyScreenState.state.responseFeedback);
            }
            if (historyScreenState.state.gameScores) {
                setCurrentGameScores(historyScreenState.state.gameScores);
            }
            if (historyScreenState.state.timeLeft) {
                setGameTimer(historyScreenState.state.timeLeft);
            }
            if (historyScreenState.state.performance) {
                setArgumentsCompleted(historyScreenState.state.performance);
            }
        }
    }, [historyScreenState, setCurrentGameScores, setResponseFeedback]);

    const speakerPoint = useCallback(direction => {
        const bodyDirection =
            direction === 'rightUp'
                ? 'right'
                : direction === 'leftUp'
                ? 'left'
                : direction;
        moveSpeaker(bodyDirection, direction);

        speakerAnimation.current.mpStand(direction);
    }, []);

    useEffect(() => {
        if (speakerButtons !== null) {
            const speakerButtonsRef = speakerButtons.current;
            speakerButtonsRef.addEventListener('click', event => {
                if (
                    event.target.className === 'speaker-game_buttons' ||
                    event.target.className === 'speaker-game__buttons-header'
                ) {
                    event.preventDefault();
                    event.stopPropagation();
                }
            });
            return () =>
                speakerButtonsRef.removeEventListener('click', event => {
                    if (
                        event.target.className === 'speaker-game_buttons' ||
                        event.target.className ===
                            'speaker-game__buttons-header'
                    ) {
                        event.preventDefault();
                        event.stopPropagation();
                    }
                });
        }
    }, []);

    useEffect(() => {
        if (tutorialCompleted === true) {
            speakerPoint('forward');
        }
        if (accessibleVersion === true) {
            speakerAnimationTimerRef.current = setInterval(() => {
                setSpeakerAnimationTimer(timer => timer + 1);
            }, 1000);
            const rightPointTimeout = setTimeout(() => {
                speakerPoint('right');
                speakerAnimation.current.mpSit('forward');
            }, 1.2 * animationTimeMultiplier);
            const leftPointTimeout = setTimeout(() => {
                speakerPoint('left');
                speakerAnimation.current.mpSit('right');
            }, 2 * animationTimeMultiplier);
            const rightUpPointTimeout = setTimeout(() => {
                speakerPoint('rightUp');
                speakerAnimation.current.mpSit('left');
            }, 3 * animationTimeMultiplier);
            const rightSecondPointTimeout = setTimeout(() => {
                speakerPoint('right');
                speakerAnimation.current.mpSit('rightUp');
            }, 4 * animationTimeMultiplier);
            const leftUpPointTimeout = setTimeout(() => {
                speakerPoint('leftUp');
                speakerAnimation.current.mpSit('right');
            }, 5 * animationTimeMultiplier);
            const forwardPointTimeout = setTimeout(() => {
                speakerPoint('forward');
                speakerAnimation.current.mpSit('leftUp');
            }, 6 * animationTimeMultiplier);
            const progressUpdateTimeout = setTimeout(() => {
                speakerAnimation.current.mpSit('forward');
                completeSpeakerGame(8);
                setProgressUpdateVisible(true);
                speakerAnimation.current.destroy();
                clearInterval(speakerAnimationTimerRef.current);
            }, 7 * animationTimeMultiplier);

            return () => {
                clearTimeout(rightPointTimeout);
                clearTimeout(leftPointTimeout);
                clearTimeout(rightUpPointTimeout);
                clearTimeout(rightSecondPointTimeout);
                clearTimeout(leftUpPointTimeout);
                clearTimeout(forwardPointTimeout);
                clearTimeout(progressUpdateTimeout);
                clearInterval(speakerAnimationTimerRef.current);
            };
        } else {
            gameTimerRef.current = setInterval(() => {
                setGameTimer(timer => timer - 1);
            }, 1000);
            return () => clearInterval(gameTimerRef.current);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tutorialCompleted]);

    useEffect(() => {
        if (clickCounter > 15) {
            const nextDirection = selectRandomArrayEntry(
                pointDirections.filter(direction => direction !== activeHead),
            );
            speakerAnimation.current.mpSit(activeHead);
            speakerPoint(nextDirection);
            setActiveHead(nextDirection);
            setClickCounter(0);
            setArgumentsCompleted(argumentsCompleted + 1);
        }
    }, [activeHead, argumentsCompleted, clickCounter, speakerPoint]);

    useEffect(() => {
        if (argumentsCompleted === 8) {
            moveSpeaker('tap', 'forward');
            const progressTimeout = setTimeout(() => {
                setProgressUpdateVisible(true);
                completeSpeakerGame(argumentsCompleted);
                speakerAnimation.current.destroy();
            }, 1000);
            return () => {
                clearTimeout(progressTimeout);
            };
        }
    }, [argumentsCompleted, completeSpeakerGame]);

    useEffect(() => {
        if (gameTimer === 0) {
            completeSpeakerGame(argumentsCompleted);
            setProgressUpdateVisible(true);
            speakerAnimation.current.destroy();
        }
    }, [argumentsCompleted, completeSpeakerGame, gameTimer]);

    const moveSpeaker = (bodyDirection, headDirection) => {
        speakerAnimation.current.showHead(headDirection);
        speakerAnimation.current.showBody(bodyDirection);
    };

    const speakerGameClasses = classNames({
        'speaker-game': true,
        'speaker-game--hidden': modalVisible && !accessibleVersion,
    });

    const tapButtonClasses = classNames({
        'speaker-game__tap-button': true,

        'speaker-game__tap-button--forward': activeHead === 'forward',
        'speaker-game__tap-button--right': activeHead === 'right',
        'speaker-game__tap-button--right-up': activeHead === 'rightUp',
        'speaker-game__tap-button--left': activeHead === 'left',
        'speaker-game__tap-button--left-up': activeHead === 'leftUp',
    });

    const tapButtonInnerClasses = classNames({
        'speaker-game__tap-button-inner': true,
        'speaker-game__tap-button-inner--border': clickCounter % 2,
        'speaker-game__tap-button-inner--border-large': (clickCounter - 1) % 2,
    });

    return (
        <>
            <div className={speakerGameClasses} aria-hidden={modalVisible}>
                <div
                    className="speaker-game__buttons"
                    role="alert"
                    aria-label={
                        accessibleVersion
                            ? 'Speaker of the House Animation'
                            : 'Speaker of the House Game'
                    }
                    ref={speakerButtons}
                >
                    {!modalVisible && (
                        <div className="speaker-game__buttons-header">
                            <IconButton
                                iconName="back"
                                size="small"
                                type="essential"
                                withSpacing={true}
                                onClick={goBack}
                                isDisabled={modalVisible || accessibleVersion}
                            />
                            <IconButton
                                iconName="settings"
                                size="small"
                                type="essential"
                                withSpacing={true}
                                onClick={() =>
                                    goToSettings(gameTimer, argumentsCompleted)
                                }
                                isDisabled={modalVisible || accessibleVersion}
                            />
                        </div>
                    )}
                    {!accessibleVersion && argumentsCompleted < 8 && (
                        <>
                            <div className={tapButtonClasses}>
                                <div className={tapButtonInnerClasses}>
                                    <IconButton
                                        key={activeHead}
                                        onClick={e => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            setClickCounter(clickCounter + 1);
                                        }}
                                        iconName="tap"
                                        isDisabled={modalVisible}
                                        onCanvas
                                    />
                                </div>
                            </div>
                            <div className="speaker-game__timer">
                                <Timer
                                    timeElapsed={gameTimer}
                                    maxTime={timerLength}
                                />
                                <Paragraph
                                    fontWeight="bold"
                                    withSpacing={false}
                                >
                                    Timer
                                </Paragraph>
                            </div>
                            <div className="speaker-game__performance-tracker">
                                <PerformanceTracker
                                    currentPerformance={argumentsCompleted}
                                    maxPerformance={8}
                                    game="debate"
                                />
                                <Paragraph
                                    fontWeight="bold"
                                    withSpacing={false}
                                >
                                    Performance
                                </Paragraph>
                            </div>
                        </>
                    )}
                </div>
                {accessibleVersion && !modalVisible && (
                    <div className="speaker-game__accessible-timer">
                        <Timer
                            maxTime={questionsGame === true ? 4 : 6}
                            timeElapsed={speakerAnimationTimer}
                            orientation="horizontal"
                            countUp
                        />
                    </div>
                )}
                <canvas
                    ref={initialiseCanvas}
                    id="speakerCanvas"
                    className="speaker-game__canvas"
                    height="600"
                    width="1200"
                />
            </div>
            {!tutorialCompleted && !accessibleVersion && (
                <TutorialModalParent
                    screen="speaker"
                    onClick={() => completeSpeakerTutorial()}
                />
            )}
        </>
    );
};

export { SpeakerGameScreen };
